import { type MarketplaceAsset, type WagmiCallbacks } from '@pkg/types';
import { convertEthToWei } from '@pkg/utils';

import { CollectionChainType } from '@app/client';
import { useListStore } from '@app/store/zustand';

import { PREPARE_LIST } from './types';
import { useOrder } from './useOrder';

export interface ListOptions extends WagmiCallbacks {}

export function useList({ onError, onSuccess }: ListOptions = {}) {
    const { newOrder, getChain, getCollectibleIdentifier } = useOrder({
        onError: handleError,
        onSuccess: handleSuccess,
    });
    const { addConfirmedToken, addToken, removeToken, setPendingConfirmation, token } = useListStore();

    function prepareListing(token: MarketplaceAsset) {
        addToken(token);
    }

    function defaultErrorHandler() {
        removeToken();
        setPendingConfirmation(false);
    }

    function handleError(error?: unknown, variables?: unknown, context?: unknown) {
        return onError ? onError(error, variables, context) : defaultErrorHandler();
    }

    function defaultSuccessHandler() {
        const confirmedToken = token;
        if (confirmedToken) {
            removeToken();
            addConfirmedToken(confirmedToken);
        }
    }

    function handleSuccess(data?: unknown, variables?: unknown, context?: unknown) {
        return onSuccess ? onSuccess(data, variables, context) : defaultSuccessHandler();
    }

    async function confirmListing(price: number, allMarkets: boolean) {
        if (!token) {
            handleError();
            return;
        }

        // TODO: For chains other than ETH, this will have to be updated
        const listingPrice = convertEthToWei(price);

        await newOrder({
            settings: PREPARE_LIST,
            order: {
                allMarkets,
                chain: getChain(CollectionChainType.Ethereum),
                item: {
                    collectibleIdentifier: getCollectibleIdentifier({
                        chain: CollectionChainType.Ethereum,
                        contractAddress: token.collection.contractAddress,
                        tokenId: token.tokenId,
                    }),
                    price: String(listingPrice),
                    quantity: 1,
                },
            },
            onComplete: () => {
                setPendingConfirmation(false);
                addConfirmedToken(token);
                removeToken();
            },
            onError: handleError,
        });
    }

    return {
        confirmListing,
        prepareListing,
    };
}
